"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, FileCheck, Stethoscope, TriangleAlert as AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const reviewSteps = [{
  step: "01",
  title: "Clinical evidence audit",
  description: "Our writers cite peer-reviewed veterinary journals, institutional guidelines, and academic clinical trials rather than anecdotal opinions."
}, {
  step: "02",
  title: "DVM fact verification",
  description: "A licensed Doctor of Veterinary Medicine scrutinizes every symptom breakdown, surgical explanation, and diagnostic indicator for anatomical accuracy."
}, {
  step: "03",
  title: "Dosage & toxicity checks",
  description: "Medication dosages, plant toxicity tables, and human food safety warnings are cross-referenced directly against current pharmacological standards."
}, {
  step: "04",
  title: "Routine re-evaluation",
  description: "Every published care guide is scheduled for periodic review to reflect updated veterinary research, drug recalls, and nutritional advancements."
}];
const governanceMetrics = [{
  value: "100%",
  label: "Veterinary-reviewed health guides"
}, {
  value: "12+",
  label: "Practicing DVM contributors"
}, {
  value: "6 mo",
  label: "Routine review audit cycle"
}];
export default function MedicalReview() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  return <section id="medical-review-process" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-border text-foreground text-xs font-semibold tracking-wider uppercase mb-4">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Clinical governance</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.18] text-foreground mb-4">
            How our veterinary board reviews every guide
          </h2>
          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Before any health article goes live, practicing veterinarians audit the medical claims, verify dosage notes, and check safety advice against active clinical standards.
          </p>
        </div>

        {/* Primary Spotlight Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {/* Left Column: Image with Clinical Overlay Card */}
          <motion.div variants={fadeIn} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-muted shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/veterinarian_reviewing_records.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/veterinarian_reviewing_records.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/veterinarian_reviewing_records.png-wvc-sizes" alt="Veterinary specialist reviewing clinical pet diagnostic data" loading="lazy" decoding="async" className="w-full h-auto aspect-[4/5] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

              {/* Inset Badge */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-md bg-background/95 backdrop-blur-xs px-3 py-1.5 border border-border shadow-xs">
                <Stethoscope className="size-4 text-primary" />
                <span className="text-xs font-medium text-foreground">Practicing clinician oversight</span>
              </div>

              {/* Bottom Inset Clinical Governance Callout */}
              <div className="absolute bottom-4 inset-x-4 rounded-xl bg-background/95 backdrop-blur-xs border border-border p-4 shadow-md">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-secondary text-primary shrink-0 mt-0.5">
                    <FileCheck className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Evidence-based standard</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Every statement of fact is linked to primary veterinary research or verified clinical diagnostic protocols.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Metrics Row Below Image */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              {governanceMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="rounded-xl border border-border bg-card p-3 text-center">
                  <div className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                    {metric.value}
                  </div>
                  <div className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 leading-tight">
                    {metric.label}
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Step-by-Step Governance Pipeline */}
          <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col gap-4">
            {reviewSteps.map((item, i) => <motion.div key={item.step} variants={fadeIn} data-index={i}>
                <Card className="border border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-primary/30 hover:shadow-sm py-5">
                  <CardContent className="px-5 sm:px-6">
                    <div className="flex items-start gap-4">
                      <span className="font-mono text-xs font-semibold px-2 py-1 rounded bg-secondary text-muted-foreground shrink-0 mt-0.5">
                        {item.step}
                      </span>
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-serif text-lg sm:text-xl font-semibold text-card-foreground">
                            {item.title}
                          </h3>
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                        </div>
                        <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}

            {/* Clinical Governance Callout Motif */}
            <div className="mt-2 rounded-xl bg-card border border-border border-l-4 border-l-primary p-5 sm:p-6 shadow-xs">
              <div className="flex items-start gap-3">
                <AlertTriangle className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-foreground">
                    Emergency and toxicity guidance protocol
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Our digital resources never replace immediate in-person triage. Whenever a pet exhibits acute distress, toxic ingestion, or trauma, our guides emphasize immediate veterinary emergency care.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Bar */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild variant="default" size="default">
                <Link to="/PetHealthPage">
                  <span>Explore reviewed guides</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default">
                <Link to="/AboutPetsWhirlPage">
                  <span>View editorial standards</span>
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}